import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiShield, FiArrowRight, FiUsers, FiLock, FiSettings, FiKey,
  FiUserCheck, FiEye, FiGlobe, FiAlertTriangle
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AdminDocs = () => {
  const sections = [
    {
      icon: FiUsers,
      title: 'Role Hierarchy',
      description: 'Understand the complete role structure within your organization.',
      link: '/docs/admin/roles',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
    },
    {
      icon: FiKey,
      title: 'Permission System',
      description: 'Learn how permissions work and how to configure custom access controls.',
      link: '/docs/admin/permissions',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
    },
    {
      icon: FiGlobe,
      title: 'Organization Administration',
      description: 'Guide for Organization Admins managing their organization.',
      link: '/docs/admin/org-admin',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600'
    },
    {
      icon: FiLock,
      title: 'Security Settings',
      description: 'Configure security policies, 2FA, and access controls.',
      link: '/docs/admin/security',
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600'
    }
  ];

  const roleOverview = [
    {
      category: 'Organization Level',
      description: 'Roles within an organization workspace',
      roles: [
        { name: 'Enterprise Admin', description: 'Multi-organization management' },
        { name: 'Organization Admin', description: 'Full organization management' },
        { name: 'Department Manager', description: 'Manages specific department' },
        { name: 'Team Lead', description: 'Leads team within department' },
        { name: 'Employee', description: 'Standard organization member' }
      ]
    },
    {
      category: 'Personal Workspace',
      description: 'Roles for individual users',
      roles: [
        { name: 'Admin (Individual)', description: 'Personal admin with advanced features' },
        { name: 'User (Individual)', description: 'Basic personal workspace access' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/admin" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Admin & Permissions</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-red-100 dark:bg-red-900/50 rounded-xl mb-4">
              <FiShield className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Admin & Permissions</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Comprehensive guide to Tasklina's role-based access control (RBAC) system, 
              admin features, and security configurations.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Understanding RBAC</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tasklina uses a comprehensive Role-Based Access Control (RBAC) system that operates 
                on two levels: <strong className="text-gray-900 dark:text-white">Platform Level</strong> for system-wide management and 
                <strong className="text-gray-900 dark:text-white">Organization Level</strong> for enterprise workspace management.
              </p>
              <div className="flex items-start space-x-3 bg-white dark:bg-gray-800 rounded-xl p-4">
                <FiAlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Important</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Role assignments determine what features and data users can access. 
                    Always follow the principle of least privilege when assigning roles.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Role Overview */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Role Overview</h2>
            
            {roleOverview.map((category, index) => (
              <div key={index} className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-blue-100' : 'bg-purple-100'}`}>
                    <FiUsers className={`w-4 h-4 ${index === 0 ? 'text-blue-600' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{category.category}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                  </div>
                </div>
                
                <div className="space-y-2 ml-11">
                  {category.roles.map((role, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <span className="font-medium text-gray-900 dark:text-white">{role.name}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{role.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <Link 
              to="/docs/admin/roles" 
              className="inline-flex items-center text-blue-600 font-medium hover:underline"
            >
              View detailed role hierarchy and permissions →
            </Link>
          </section>

          {/* Sections Grid */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Documentation Sections</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <Link
                    key={index}
                    to={section.link}
                    className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md hover:border-red-200 transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl ${section.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-red-600 transition-colors">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{section.description}</p>
                    <div className="flex items-center text-red-600 text-sm font-medium">
                      <span>Learn more</span>
                      <FiArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Key Concepts */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Key Concepts</h2>
            
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiUserCheck className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Role Assignment</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Users are assigned roles that determine their access level. A user can have different 
                  roles at different levels (e.g., Platform Admin and Organization Admin).
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiKey className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Permission Inheritance</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Higher-level roles inherit permissions from lower-level roles. For example, 
                  an Organization Admin has all permissions of a Department Manager plus additional ones.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiEye className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Scope & Visibility</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Permissions are scoped to specific contexts. A Department Manager can only manage 
                  their assigned department, not the entire organization.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiSettings className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Custom Permissions</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Organizations can create custom roles with specific permission sets to match 
                  their unique workflow requirements.
                </p>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Common Tasks</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <div className="space-y-3">
                <Link 
                  to="/docs/admin/roles#assign-role"
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-sm transition-shadow"
                >
                  <span className="text-gray-700 dark:text-gray-200">How to assign roles to users</span>
                  <FiArrowRight className="text-gray-400 dark:text-gray-500" />
                </Link>
                <Link 
                  to="/docs/admin/permissions#create-custom"
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-sm transition-shadow"
                >
                  <span className="text-gray-700 dark:text-gray-200">Create custom permission templates</span>
                  <FiArrowRight className="text-gray-400 dark:text-gray-500" />
                </Link>
                <Link 
                  to="/docs/admin/security#2fa"
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-sm transition-shadow"
                >
                  <span className="text-gray-700 dark:text-gray-200">Enable two-factor authentication</span>
                  <FiArrowRight className="text-gray-400 dark:text-gray-500" />
                </Link>
                <Link 
                  to="/docs/admin/security#audit"
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-sm transition-shadow"
                >
                  <span className="text-gray-700 dark:text-gray-200">View audit logs</span>
                  <FiArrowRight className="text-gray-400 dark:text-gray-500" />
                </Link>
              </div>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AdminDocs;
